import React, { useState } from 'react';
import { Upload, FileText, Trash2, CheckCircle, XCircle, Loader } from 'lucide-react';

const API_URL = 'http://localhost:8000';

const AdminPDFUpload = () => {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('law');
  const [source, setSource] = useState('');
  const [hierarchyLevel, setHierarchyLevel] = useState(1);
  const [language, setLanguage] = useState('en');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [documents, setDocuments] = useState([]);

  const docTypes = [
    { value: 'law', label: 'Law (VAT Act)', hierarchy: 1 },
    { value: 'ordinance', label: 'Ordinance (VAT Ordinance)', hierarchy: 2 },
    { value: 'tax_authority_practice', label: 'FTA Practice', hierarchy: 3 },
    { value: 'case_law', label: 'Case Law', hierarchy: 4 },
    { value: 'example', label: 'Practical Example', hierarchy: 5 }
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'de', label: 'German (Deutsch)' },
    { value: 'fr', label: 'French (Français)' },
    { value: 'it', label: 'Italian (Italiano)' }
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setUploadResult(null);
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleDocTypeChange = (e) => {
    const type = e.target.value;
    setDocType(type);
    const selectedType = docTypes.find(t => t.value === type);
    if (selectedType) {
      setHierarchyLevel(selectedType.hierarchy);
    }
  };

  const handleUpload = async () => {
    if (!file || !source.trim()) {
      alert('Please select a file and provide a source name');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    formData.append('source', source);
    formData.append('hierarchy_level', hierarchyLevel.toString());
    formData.append('language', language);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/upload-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setUploadResult({
          success: true,
          message: data.message,
          chunks: data.chunks_created
        });
        
        // Reset form
        setFile(null);
        setSource('');
        document.getElementById('file-input').value = '';
        
        // Refresh documents list
        fetchDocuments();
      } else {
        setUploadResult({
          success: false,
          message: data.detail || 'Upload failed'
        });
      }
    } catch (error) {
      setUploadResult({
        success: false,
        message: `Error: ${error.message}`
      });
    } finally {
      setUploading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            📚 Knowledge Base Management
          </h1>
          <p className="text-slate-600">
            Upload PDF documents to automatically update the VAT knowledge base
          </p>
        </div>

        {/* Upload Form */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Upload New Document</h2>
          
          <div className="space-y-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                PDF File
              </label>
              <div className="flex items-center gap-3">
                <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-red-400 transition-colors">
                  <Upload className="w-5 h-5 text-slate-400 mr-2" />
                  <span className="text-sm text-slate-600">
                    {file ? file.name : 'Choose PDF file'}
                  </span>
                  <input
                    id="file-input"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {file && (
                  <button
                    onClick={() => {
                      setFile(null);
                      document.getElementById('file-input').value = '';
                    }}
                    className="p-2 text-slate-400 hover:text-red-600"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Document Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Document Type
              </label>
              <select
                value={docType}
                onChange={handleDocTypeChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {docTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Source / Document Name
              </label>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="e.g., Swiss VAT Act 2024, FTA Circular 2025-01"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Language */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Language
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {languages.map(lang => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Hierarchy Level */}
            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Hierarchy Level:</span>
                <span className="font-semibold text-slate-900">{hierarchyLevel}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                1 = Law (highest priority) → 5 = Examples (lowest priority)
              </p>
            </div>

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={!file || !source.trim() || uploading}
              className="w-full py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-semibold flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Processing PDF...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload & Process
                </>
              )}
            </button>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div className={`mt-4 p-4 rounded-lg ${
              uploadResult.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {uploadResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-semibold ${
                    uploadResult.success ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {uploadResult.success ? 'Success!' : 'Error'}
                  </p>
                  <p className={`text-sm mt-1 ${
                    uploadResult.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {uploadResult.message}
                  </p>
                  {uploadResult.success && uploadResult.chunks && (
                    <p className="text-sm text-green-600 mt-1">
                      Created {uploadResult.chunks} searchable chunks
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* How it Works */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            🔧 How It Works
          </h2>
          <div className="space-y-3 text-sm text-slate-600">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <div>
                <strong>Extract:</strong> Text is extracted from your PDF, preserving structure
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div>
                <strong>Chunk:</strong> Document is split intelligently by articles, sections, or paragraphs
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <div>
                <strong>Embed:</strong> Each chunk is converted to vector embeddings for semantic search
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-semibold">
                4
              </div>
              <div>
                <strong>Index:</strong> Stored in vector database (Pinecone) for fast retrieval
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-semibold">
                5
              </div>
              <div>
                <strong>Search:</strong> When users ask questions, most relevant chunks are retrieved automatically
              </div>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">💡 Tips for Best Results</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Use clear, descriptive source names (e.g., "FTA Circular 2025-01 - E-commerce")</li>
            <li>• Upload text-based PDFs, not scanned images (OCR coming soon)</li>
            <li>• For large documents, processing may take 1-2 minutes</li>
            <li>• Documents are automatically deduplicated if you upload the same file twice</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminPDFUpload;
